/**
 * Drizzle SQLite table definitions for the local library DB.
 *
 * DDL is in db.ts; this file drives the type-safe query builder only.
 *
 * @docLink packages/library/concepts#schema
 */

import { integer, sqliteTable, text } from "drizzle-orm/sqlite-core";

// ---------------------------------------------------------------------------
// Libraries (formerly `sources`)
// ---------------------------------------------------------------------------

/**
 * Drizzle table definition for the `libraries` table.
 *
 * Renamed from `sources` 2026-05-13. Adds: name, backend, backendConfig,
 * ownership, structure, isDefault, manifestGenerated, updatedAt.
 * Removes: type, url, ref, sidecarPath.
 */
export const libraries = sqliteTable("libraries", {
  /** UUID primary key. */
  id: text("id").primaryKey(),
  /** User-visible handle; unique. */
  name: text("name").notNull(),
  /** Absolute on-disk root. */
  path: text("path").notNull(),
  /** Backend kind: `local | git | store`. */
  backend: text("backend").notNull(),
  /** Backend-specific JSON config; stored as text, parsed on read. */
  backendConfig: text("backend_config").notNull().default("{}"),
  /** Ownership: `owner | contributor | reader`. */
  ownership: text("ownership").notNull(),
  /** Layout: `flat | domain` or null when not yet detected. */
  structure: text("structure"),
  /** Boolean (0/1) — true iff this is the auto-target for `asset migrate`. */
  isDefault: integer("is_default", { mode: "boolean" }).notNull().default(false),
  /** Boolean (0/1) — true iff the CLI generated the on-disk manifest. */
  manifestGenerated: integer("manifest_generated", { mode: "boolean" }).notNull().default(false),
  /** Last sync ISO timestamp. */
  lastSyncAt: text("last_sync_at"),
  /** Creation ISO timestamp. */
  createdAt: text("created_at").notNull(),
  /** Last-update ISO timestamp. */
  updatedAt: text("updated_at").notNull(),
});

// ---------------------------------------------------------------------------
// Asset definitions
// ---------------------------------------------------------------------------

/**
 * FK `library_id -> libraries(id) ON DELETE SET NULL` (renamed from source_id).
 */
export const assetDefinitions = sqliteTable("asset_definitions", {
  id: text("id").primaryKey(),
  kind: text("kind").notNull(),
  publisher: text("publisher").notNull(),
  name: text("name").notNull(),
  version: text("version").notNull(),
  sha256: text("sha256"),
  description: text("description"),
  license: text("license"),
  category: text("category"),
  manifest: text("manifest").notNull(),
  /** FK to the library that provided this definition. */
  libraryId: text("library_id"),
  /** JSON-encoded array of this asset's body-link soft-dep target refs. */
  softDeps: text("soft_deps"),
  kindProviderVersion: text("kind_provider_version"),
  cachedAt: text("cached_at").notNull(),
  updatedAt: text("updated_at").notNull(),
});

// ---------------------------------------------------------------------------
// Instances (unchanged)
// ---------------------------------------------------------------------------

export const instances = sqliteTable("instances", {
  id: text("id").primaryKey(),
  defRef: text("def_ref").notNull(),
  defPin: text("def_pin").notNull(),
  config: text("config").notNull().default("{}"),
  credentialRef: text("credential_ref"),
  createdAt: text("created_at").notNull(),
  updatedAt: text("updated_at").notNull(),
  createdBy: text("created_by"),
  sourceCommitSha: text("source_commit_sha"),
});

// ---------------------------------------------------------------------------
// Assignments (renamed from subscriptions 2026-06-08)
// ---------------------------------------------------------------------------

export const assignments = sqliteTable("assignments", {
  id: text("id").primaryKey(),
  workspaceId: text("workspace_id").notNull(),
  instanceId: text("instance_id").notNull(),
  pinPolicy: text("pin_policy").notNull(),
  assignedAt: text("assigned_at").notNull(),
});
